import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { HexColorPicker } from "react-colorful";
import { toast } from "react-toastify";
import { Info } from "../../../__generated__/graphql";
import { LoadingOverlay } from "../../../components/Loading";
import { PageContainer } from "../../../components/PageContainer";
import { UPDATE_INFO } from "../graphql";
import * as yup from "yup";
import { UpdateMiscConfigButton } from "../../links/updateMiscConfig";

const schema = yup.object({
  title: yup.string().required("Section title is required"),
  headerColor: yup.string().nullable(),
});

type InfoFormValues = {
  title: string;
  headerColor: string | null | undefined;
};

export const EditInfoForm = ({
  info,
  refetch,
}: {
  info: Info;
  refetch: () => void;
}) => {
  const [updateInfo] = useMutation(UPDATE_INFO, {
    onCompleted: () => {
      refetch();
      toast("Update complete");
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InfoFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: info.title ?? "Untitled section",
      headerColor: info.headerColor ?? "",
    },
  });

  const headerColorValue = watch("headerColor") ?? "";
  const pickerColor = useMemo(
    () => (headerColorValue && headerColorValue.trim() ? headerColorValue : "#4285f4"),
    [headerColorValue]
  );

  const onSubmit = async ({ title, headerColor }: InfoFormValues) => {
    await updateInfo({
      // @ts-ignore
      variables: {
        ...info,
        title,
        order: info.order ?? 0,
        hidden: info.hidden ?? false,
        headerColor: headerColor?.trim() ? headerColor : null,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageContainer
        title="Information Section Settings"
        actions={
          <>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
            <UpdateMiscConfigButton />
          </>
        }
      >
        <LoadingOverlay loading={false}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This defines the section title. Subsection content is managed
              below in Subsections.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Section title"
                error={!!errors.title?.message}
                helperText={
                  errors.title?.message ??
                  "Shown as the heading users see for this information section."
                }
                {...register("title")}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Header Color"
                placeholder="#4a90e2"
                error={!!errors.headerColor?.message}
                helperText={
                  errors.headerColor?.message ??
                  "Optional accent color for the section header."
                }
                {...register("headerColor")}
              />

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box
                  sx={{
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                  <HexColorPicker
                    color={pickerColor}
                    onChange={(color) => {
                      setValue("headerColor", color, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </Box>

                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      width: 140,
                      height: 36,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: pickerColor,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {pickerColor}
                  </Typography>
                </Stack>
              </Stack>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Visibility
                </Typography>
                <Typography variant="body2">
                  {info.hidden
                    ? "This information section is hidden in the app."
                    : "This information section is visible in the app."}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </LoadingOverlay>
      </PageContainer>
    </form>
  );
};
